import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faSearch, faRotateLeft, faBalanceScale, faCalendarDay, faCoins, faFileInvoiceDollar, faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../../api';
import { formatCurrency } from '../../utils/formatters';
import './AnalystPage.css';

const METHODS = [
  ['all', 'All'], ['petty_cash', 'Petty Cash'], ['cash_expenses', 'Cash Expenses'],
  ['bank_charges', 'Bank Charges'], ['hidden_expenses', 'Hidden Expenses'],
  ['purchasing', 'Purchasing'], ['other_expenses', 'Other Expenses']
];
const COMPANIES = [
  ['all', 'All Companies'], ['JH', 'JH'], ['RD', 'RD']
];
const initialRange = { fromDate: '', toDate: '' };
const formatDate = (value) => {
  if (!value) return '-';
  const raw = String(value);
  // Plain 'YYYY-MM-DD' (what the API returns) is formatted as-is, never via
  // Date, so no timezone conversion can move it to another day.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-');
    return `${day}/${month}/${year}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}/${parsed.getFullYear()}`;
};

const AnalystPage = () => {
  const page = useLocation().pathname.split('/').pop();
  const isIncome = page === 'income' || page === 'incomes';
  const isBalanceSheet = page === 'balance-sheet';

  const [range, setRange] = useState(initialRange);
  const [method, setMethod] = useState('all');
  const [company, setCompany] = useState('all');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const balanceRequestRef = useRef(0);
  const reportRequestRef = useRef(0);

  const updateRange = (event) => setRange(current => ({ ...current, [event.target.name]: event.target.value }));
  const validateRange = () => range.fromDate && range.toDate && range.fromDate > range.toDate ? 'From Date cannot be after To Date.' : '';

  const loadReport = useCallback(async () => {
    const requestId = ++reportRequestRef.current;
    const validation = validateRange();
    if (validation) {
      setError(validation);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const endpoint = isIncome ? '/analyst/incomes' : '/analyst/expenses';
      const params = {
        ...(range.fromDate && { fromDate: range.fromDate }),
        ...(range.toDate && { toDate: range.toDate })
      };
      if (isIncome) {
        if (company && company !== 'all') params.company = company;
      } else {
        params.method = method;
      }
      const response = await api.get(endpoint, { params });
      if (requestId !== reportRequestRef.current) return;
      setRecords(response.data.records || []);
      setSummary(response.data);
    } catch (requestError) {
      if (requestId !== reportRequestRef.current) return;
      setError(requestError.response?.data?.error || 'Unable to load the report. Please try again.');
      setRecords([]);
      setSummary(null);
    } finally {
      if (requestId === reportRequestRef.current) setLoading(false);
    }
  }, [range, isIncome, company, method]);

  useEffect(() => {
    if (!isBalanceSheet) {
      setRange(initialRange);
      setMethod('all');
      setCompany('all');
      setSearch('');
      setStatus('all');
      setError('');
      const endpoint = isIncome ? '/analyst/incomes' : '/analyst/expenses';
      const requestId = ++reportRequestRef.current;
      setLoading(true);
      api.get(endpoint)
        .then(res => {
          if (requestId !== reportRequestRef.current) return;
          setRecords(res.data.records || []);
          setSummary(res.data);
        })
        .catch(err => {
          if (requestId !== reportRequestRef.current) return;
          setError(err.response?.data?.error || 'Unable to load the report. Please try again.');
          setRecords([]);
          setSummary(null);
        })
        .finally(() => {
          if (requestId === reportRequestRef.current) setLoading(false);
        });
    }
  }, [isIncome, isBalanceSheet]);

  const clearReport = () => {
    const requestId = ++reportRequestRef.current;
    setRange(initialRange);
    setMethod('all');
    setCompany('all');
    setSearch('');
    setStatus('all');
    setError('');
    const endpoint = isIncome ? '/analyst/incomes' : '/analyst/expenses';
    setLoading(true);
    api.get(endpoint)
      .then(res => {
        if (requestId !== reportRequestRef.current) return;
        setRecords(res.data.records || []);
        setSummary(res.data);
      })
      .catch(err => {
        if (requestId !== reportRequestRef.current) return;
        setError(err.response?.data?.error || 'Unable to load the report. Please try again.');
        setRecords([]);
        setSummary(null);
      })
      .finally(() => {
        if (requestId === reportRequestRef.current) setLoading(false);
      });
  };

  const loadBalance = useCallback(async () => {
    const requestId = ++balanceRequestRef.current;
    if (!selectedDate) {
      setLoading(false);
      setBalance(null);
      setError('Select a date to view the balance sheet.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.get('/analyst/balance-sheet', {
        params: { date: selectedDate },
        timeout: 30000
      });
      if (requestId !== balanceRequestRef.current) return;
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('The Balance Sheet response was empty.');
      }
      setBalance(response.data);
    } catch (requestError) {
      if (requestId !== balanceRequestRef.current) return;
      const message = requestError.code === 'ECONNABORTED'
        ? 'The Balance Sheet request timed out. Please try again.'
        : requestError.response?.data?.error || requestError.message || 'Unable to calculate the balance sheet.';
      setError(message);
      setBalance(null);
    } finally {
      if (requestId === balanceRequestRef.current) setLoading(false);
    }
  }, [selectedDate]);
  useEffect(() => {
    if (isBalanceSheet && selectedDate) loadBalance();
    return () => { balanceRequestRef.current += 1; };
  }, [isBalanceSheet, selectedDate, loadBalance]);
  useEffect(() => {
    if (!isBalanceSheet) return undefined;
    const refreshBalance = () => {
      if (selectedDate) loadBalance();
    };
    window.addEventListener('purchase-payment-updated', refreshBalance);
    return () => window.removeEventListener('purchase-payment-updated', refreshBalance);
  }, [isBalanceSheet, selectedDate, loadBalance]);
  const clearBalance = () => {
    balanceRequestRef.current += 1;
    setSelectedDate('');
    setBalance(null);
    setError('');
    setLoading(false);
  };

  // Client-side only: every field used below is already in the records the
  // server returns for the selected date range / method, so Search, Company
  // and Status narrow the same data instead of round-tripping the server.
  // Display-only on THIS page: Bank Charges / Cash Expenses ('Recorded') and
  // Hidden Expenses ('Hidden') are money already spent, same as 'Paid' - but
  // the raw status string stays untouched on the server (shared loadExpenseRecords
  // also feeds the Expense Summary Report, which keeps its own labels).
  const displayStatus = (rawStatus) => (rawStatus === 'Recorded' || rawStatus === 'Hidden') ? 'Paid' : rawStatus;

  const expenseStatusOptions = useMemo(() => (
    isIncome ? [] : [...new Set(records.map((record) => displayStatus(record.status)).filter(Boolean))].sort()
  ), [isIncome, records]);

  const filteredRecords = useMemo(() => {
    if (isIncome) return records;
    const term = search.trim().toLowerCase();
    return records.filter((record) => {
      if (company !== 'all' && record.company_name !== company) return false;
      if (status !== 'all' && displayStatus(record.status) !== status) return false;
      if (term) {
        const haystack = [record.reference, record.description, record.expense_type, record.sub_expense_type, record.company_name, record.payment_source]
          .map((value) => String(value || '').toLowerCase());
        if (!haystack.some((value) => value.includes(term))) return false;
      }
      return true;
    });
  }, [isIncome, records, company, status, search]);

  const filteredTotalExpenses = useMemo(() =>
    filteredRecords.reduce((sum, record) => sum + (Number(record.amount) || 0), 0),
  [filteredRecords]);

  if (isBalanceSheet) return <BalanceSheet selectedDate={selectedDate} setSelectedDate={setSelectedDate} balance={balance} loading={loading} error={error} loadBalance={loadBalance} clearBalance={clearBalance} />;
  return (
    <div className="page-container analyst-page">
      <header className="page-header analyst-header">
        <div>
          <h2><FontAwesomeIcon icon={faChartLine} /> Analyst / {isIncome ? 'Income' : 'Expenses'}</h2>
        </div>
      </header>
      <div className="card analyst-filters">
        <label>From Date<input type="date" name="fromDate" value={range.fromDate} onChange={updateRange} /></label>
        <label>To Date<input type="date" name="toDate" value={range.toDate} onChange={updateRange} /></label>
        {isIncome ? (
          <label>Company<select value={company} onChange={event => setCompany(event.target.value)}>{COMPANIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        ) : (
          <>
            <label>Expense Method<select value={method} onChange={event => setMethod(event.target.value)}>{METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label>Company<select value={company} onChange={event => setCompany(event.target.value)}>{COMPANIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label>Status<select value={status} onChange={event => setStatus(event.target.value)}>
              <option value="all">All (Paid &amp; Unpaid)</option>
              {expenseStatusOptions.map(value => <option key={value} value={value}>{value}</option>)}
            </select></label>
            <label>Search<input type="text" value={search} onChange={event => setSearch(event.target.value)} placeholder="Reference, description, type..." /></label>
          </>
        )}
        <div className="analyst-actions">
          <button className="btn btn-primary" onClick={loadReport} disabled={loading}><FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} /> Apply</button>
          <button className="btn btn-secondary" onClick={clearReport} disabled={loading}><FontAwesomeIcon icon={faRotateLeft} /> Clear</button>
        </div>
      </div>
      {error && <div className="analyst-error" role="alert">{error}</div>}
      <div className="analyst-summary-grid">
        <SummaryCard label={isIncome ? 'Total Income' : 'Total Expenses'} value={isIncome ? summary?.totalIncome : filteredTotalExpenses} />
        <SummaryCard label="Transactions" value={(isIncome ? summary?.transactionCount : filteredRecords.length) || 0} numeric />
        <SummaryCard label={isIncome ? 'Selected Company' : 'Selected Method'} value={isIncome ? COMPANIES.find(item => item[0] === company)?.[1] : METHODS.find(item => item[0] === method)?.[1]} />
      </div>
      <div className="card analyst-table-card">
        <div className="analyst-table-scroll">
          <table className={`report-table ${isIncome ? '' : 'analyst-expenses-table'}`}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference No</th>
                {isIncome ? (
                  <>
                    <th>Company</th>
                    <th>Department</th>
                  </>
                ) : (
                  <>
                    <th>Expense Method</th>
                    <th>Expense Type</th>
                    <th>Sub Expense Type</th>
                    <th>Company</th>
                    <th>Payment Type</th>
                    <th>Description</th>
                    <th>Status</th>
                  </>
                )}
                <th className="analyst-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length ? filteredRecords.map(record => (
                <tr key={isIncome ? `inc-${record.id}` : `${record.expense_method}-${record.id}`}>
                  <td>{formatDate(record.date || record.income_date)}</td>
                  <td>{record.reference || (isIncome ? `INC-${record.id}` : '-')}</td>
                  {isIncome ? (
                    <>
                      <td><span className="analyst-badge">{record.company_name}</span></td>
                      <td>{record.department_name}</td>
                    </>
                  ) : (
                    <>
                      <td><span className="analyst-badge">{record.expense_method}</span></td>
                      <td>{record.expense_type || '-'}</td>
                      <td>{record.sub_expense_type || '-'}</td>
                      <td>{record.company_name || '-'}</td>
                      <td>{record.payment_source || '-'}</td>
                      <td>{record.description || '-'}</td>
                      <td>{displayStatus(record.status) || '-'}</td>
                    </>
                  )}
                  <td className="analyst-amount">{formatCurrency(record.amount)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={isIncome ? 5 : 10} className="analyst-empty">
                    {isIncome ? 'No income records found for the selected filters.' : 'No expense records found for the selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, numeric }) => <div className="card analyst-summary-card"><span>{label}</span><strong>{numeric ? value : value === undefined ? formatCurrency(0) : typeof value === 'number' ? formatCurrency(value) : value}</strong></div>;

const BsRow = ({ label, value, tone, indent, showAmount = true }) => (
  <tr className={['bs-row', tone && `bs-row--${tone}`, indent && 'bs-row--indent'].filter(Boolean).join(' ')}>
    <td className="bs-row-label">{label}</td>
    <td className="bs-row-amount">{showAmount ? formatCurrency(value) : ''}</td>
  </tr>
);
const BsSubheadRow = ({ label }) => (
  <tr className="bs-row bs-row--subhead"><td colSpan={2}>{label}</td></tr>
);
const BsGroupHeadRow = ({ label }) => (
  <tr className="bs-row bs-row--group"><td colSpan={2}>{label}</td></tr>
);

// Shared row model behind both the on-screen tables and the PDF / Excel
// export, so what you see is exactly what you export.
const buildAssetsSections = (balance) => ([
  {
    heading: '1. Cash and Cash Equivalents',
    rows: [
      { label: 'Cash Book - JH', value: balance.cashBookJH },
      { label: 'Cash Book - RD', value: balance.cashBookRD }
    ],
    subtotalLabel: 'Total Cash Book', subtotalValue: balance.cashBookTotal
  },
  {
    heading: '2. Inventory / Stock Value',
    rows: [{ label: 'Stock Value', value: balance.storesValue }]
  },
  {
    heading: '3. Bank Balances',
    rows: (balance.banks || []).map((bank, index) => ({
      key: bank.id || index,
      label: `${bank.bank_name} - ${bank.account_number}${bank.company ? ` (${bank.company})` : ''}`,
      value: bank.balance
    })),
    subtotalLabel: 'Total Bank Balances', subtotalValue: balance.totalBankValues
  },
  {
    heading: '4. Supplier Advances & Supplier Credit Balance',
    rows: [
      { label: 'Purchasing Advance Payment Balance', value: balance.purchasingAdvancePaymentBalance },
      { label: 'Overpayment / Unallocated Credit', value: balance.purchasingOverpayment }
    ],
    subtotalLabel: 'Total Supplier Credits', subtotalValue: balance.totalSupplierCredits
  },
  {
    heading: '5. Other Ledgers',
    rows: (balance.otherLedgers || []).map((ledger) => ({ key: ledger.id, label: ledger.name, value: ledger.balance })),
    subtotalLabel: 'Total of Other Ledgers', subtotalValue: balance.totalOtherLedgers
  },
  {
    heading: '6. Card Ledger',
    rows: [{ label: 'Pending Card Receivable (not yet realised to bank)', value: balance.cardLedgerReceivable }]
  },
  {
    heading: '7. Staff Loans',
    rows: [{ label: 'Staff Loan Receivable (outstanding)', value: balance.staffLoanReceivable }]
  }
]);

const buildLiabilitiesGroups = (balance) => ([
  {
    heading: '1. Current Liabilities',
    rows: [
      { label: `Pending / Outstanding Supplier Invoices (${balance.pendingPurchasingCount || 0} invoices)`, value: balance.totalPurchasingPending },
      { label: 'New Expenses Payable (VAT / other unpaid bills)', value: balance.newExpensesPayable },
      { label: 'Salary Payable (Existing Salary Module)', value: balance.salaryPayable },
      { label: 'EPF Payable (Existing Salary Module)', value: balance.epfPayable },
      { label: 'ETF Payable (Existing Salary Module)', value: balance.etfPayable }
    ],
    subtotalLabel: 'Total Current Liabilities', subtotalValue: balance.currentLiabilities
  },
  {
    heading: '2. Non-Current Liabilities',
    rows: (balance.bankLoans || []).map((loan, index) => ({
      key: loan.id || index,
      label: `${loan.bank_name}${loan.account_number ? ` - ${loan.account_number}` : ''}${loan.company ? ` (${loan.company})` : ''}`,
      value: loan.balance
    })),
    subtotalLabel: 'Total Non-Current Liabilities', subtotalValue: balance.nonCurrentLiabilities
  }
]);

const BalanceSheet = ({ selectedDate, setSelectedDate, balance, loading, error, loadBalance, clearBalance }) => {
  const netPositive = !balance || Number(balance.netBalance) >= 0;
  const assetsSections = balance ? buildAssetsSections(balance) : [];
  const liabilitiesGroups = balance ? buildLiabilitiesGroups(balance) : [];

  const exportFileName = `balance-sheet-${selectedDate || 'export'}`;

  const handleExportExcel = () => {
    if (!balance) return;
    const sheet = [['Balance Sheet'], [`As of ${formatDate(selectedDate)}`], []];
    sheet.push(['ASSETS', '']);
    assetsSections.forEach((section) => {
      sheet.push([section.heading, '']);
      section.rows.forEach((row) => sheet.push([row.label, Number(row.value || 0)]));
      if (section.subtotalLabel) sheet.push([section.subtotalLabel, Number(section.subtotalValue || 0)]);
    });
    sheet.push(['Total Assets', Number(balance.totalAssets || 0)], []);
    sheet.push(['LIABILITIES', '']);
    liabilitiesGroups.forEach((group) => {
      sheet.push([group.heading, '']);
      group.rows.forEach((row) => sheet.push([row.label, Number(row.value || 0)]));
      sheet.push([group.subtotalLabel, Number(group.subtotalValue || 0)]);
    });
    sheet.push(['Total Liabilities', Number(balance.totalLiabilities || 0)], []);
    sheet.push(['Net Balance', Number(balance.netBalance || 0)]);

    const worksheet = XLSX.utils.aoa_to_sheet(sheet);
    worksheet['!cols'] = [{ wch: 50 }, { wch: 18 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');
    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
  };

  const handleExportPdf = () => {
    if (!balance) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Balance Sheet', 15, 15);
    doc.setFontSize(10);
    doc.text(`As of ${formatDate(selectedDate)}`, 15, 22);

    const toBody = (sections, isGroup) => {
      const body = [];
      sections.forEach((section) => {
        body.push([{ content: section.heading, styles: { fontStyle: 'bold', fillColor: [243, 238, 248] } }, '']);
        section.rows.forEach((row) => body.push([row.label, formatCurrency(row.value)]));
        const subtotalLabel = isGroup ? section.subtotalLabel : section.subtotalLabel;
        if (subtotalLabel) {
          body.push([{ content: subtotalLabel, styles: { fontStyle: 'bold' } }, { content: formatCurrency(section.subtotalValue), styles: { fontStyle: 'bold' } }]);
        }
      });
      return body;
    };

    autoTable(doc, {
      head: [['Assets', 'Amount']],
      body: [...toBody(assetsSections), [{ content: 'Total Assets', styles: { fontStyle: 'bold' } }, { content: formatCurrency(balance.totalAssets), styles: { fontStyle: 'bold' } }]],
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 31, 94] },
      columnStyles: { 1: { halign: 'right' } }
    });

    autoTable(doc, {
      head: [['Liabilities', 'Amount']],
      body: [...toBody(liabilitiesGroups, true), [{ content: 'Total Liabilities', styles: { fontStyle: 'bold' } }, { content: formatCurrency(balance.totalLiabilities), styles: { fontStyle: 'bold' } }]],
      startY: doc.lastAutoTable.finalY + 8,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [143, 29, 44] },
      columnStyles: { 1: { halign: 'right' } }
    });

    doc.setFontSize(11);
    doc.text(`Net Balance: ${formatCurrency(balance.netBalance)}`, 15, doc.lastAutoTable.finalY + 10);
    doc.save(`${exportFileName}.pdf`);
  };

  return (
    <div className="page-container balance-sheet-page">
      <div className="bs-title-bar">
        <div className="bs-title-main">
          <FontAwesomeIcon icon={faBalanceScale} />
          <h2>Balance Sheet</h2>
        </div>
        {balance && selectedDate && <span className="bs-as-of">As of {formatDate(selectedDate)}</span>}
      </div>

      <div className="card analyst-filters">
        <label>
          <FontAwesomeIcon icon={faCalendarDay} /> Select Date
          <input type="date" value={selectedDate} onChange={event => setSelectedDate(event.target.value)} />
        </label>
        <div className="analyst-actions">
          <button className="btn btn-primary" onClick={loadBalance} disabled={loading || !selectedDate}><FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} /> Refresh</button>
          <button className="btn btn-secondary" onClick={clearBalance} disabled={loading}><FontAwesomeIcon icon={faRotateLeft} /> Clear</button>
          <button className="btn btn-secondary" onClick={handleExportPdf} disabled={!balance}><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
          <button className="btn btn-success" onClick={handleExportExcel} disabled={!balance}><FontAwesomeIcon icon={faFileExcel} /> Excel</button>
        </div>
      </div>

      {error && <div className="analyst-error" role="alert">{error}</div>}

      {!balance ? (
        <div className="analyst-empty-panel">{error || 'Select a date to view the balance sheet.'}</div>
      ) : (
        <>
          <div className="bs-kpi-grid">
            <div className="bs-kpi-card bs-kpi-card--assets">
              <span><FontAwesomeIcon icon={faCoins} /> Total Assets</span>
              <strong>{formatCurrency(balance.totalAssets)}</strong>
            </div>
            <div className="bs-kpi-card bs-kpi-card--liabilities">
              <span><FontAwesomeIcon icon={faFileInvoiceDollar} /> Total Liabilities</span>
              <strong>{formatCurrency(balance.totalLiabilities)}</strong>
            </div>
            <div className={`bs-kpi-card bs-kpi-card--net ${netPositive ? 'is-positive' : 'is-negative'}`}>
              <span><FontAwesomeIcon icon={faBalanceScale} /> Net Balance</span>
              <strong>{formatCurrency(balance.netBalance)}</strong>
            </div>
          </div>

          <div className="bs-columns">
            <div className="card bs-column-card">
              <div className="bs-column-header bs-column-header--assets">
                <FontAwesomeIcon icon={faCoins} /> Assets
              </div>
              <div className="bs-table-scroll">
                <table className="bs-table">
                  <tbody>
                    {assetsSections.map((section) => (
                      <React.Fragment key={section.heading}>
                        <BsSubheadRow label={section.heading} />
                        {section.rows.map((row, index) => (
                          <BsRow key={row.key ?? `${section.heading}-${index}`} label={row.label} value={row.value} indent />
                        ))}
                        {section.subtotalLabel && (
                          <BsRow label={section.subtotalLabel} value={section.subtotalValue} tone="subtotal" indent />
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bs-row bs-row--total">
                      <td className="bs-row-label">Total Assets</td>
                      <td className="bs-row-amount">{formatCurrency(balance.totalAssets)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="card bs-column-card">
              <div className="bs-column-header bs-column-header--liabilities">
                <FontAwesomeIcon icon={faFileInvoiceDollar} /> Liabilities
              </div>
              <div className="bs-table-scroll">
                <table className="bs-table">
                  <tbody>
                    {liabilitiesGroups.map((group) => (
                      <React.Fragment key={group.heading}>
                        <BsGroupHeadRow label={group.heading} />
                        {group.rows.map((row, index) => (
                          <BsRow key={row.key ?? `${group.heading}-${index}`} label={row.label} value={row.value} indent />
                        ))}
                        <BsRow label={group.subtotalLabel} value={group.subtotalValue} tone="subtotal" indent />
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bs-row bs-row--total">
                      <td className="bs-row-label">Total Liabilities</td>
                      <td className="bs-row-amount">{formatCurrency(balance.totalLiabilities)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <div className={`bs-net-strip ${netPositive ? 'is-positive' : 'is-negative'}`}>
            <span>Net Balance</span>
            <strong>{formatCurrency(balance.netBalance)}</strong>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalystPage;
